import { useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { FormError } from "./FormBits";

const Steps = ({ steps, current }) => (
  <ol className="flex gap-2 mb-8" aria-label="Progress">
    {steps.map((s, i) => (
      <li key={s.title} className="flex-1" aria-current={i === current ? "step" : undefined}>
        <span className={`block h-1.5 rounded-full transition-colors duration-300 ${i <= current ? "bg-[#1E3A2B]" : "bg-[#E5E0D8]"}`} />
        <span className={`hidden md:block text-[11px] mt-2 font-semibold uppercase tracking-wider ${i === current ? "text-[#1E3A2B]" : "text-[#8A938F]"}`}>{s.short || s.title}</span>
      </li>
    ))}
  </ol>
);

export const Wizard = ({ steps, onSubmit, submitting, error, testPrefix, submitLabel = "Submit" }) => {
  const [i, setI] = useState(0);
  const [localErr, setLocalErr] = useState("");
  const topRef = useRef(null);
  const step = steps[i];
  const last = i === steps.length - 1;

  const go = (n) => {
    setLocalErr("");
    setI(n);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const advance = () => {
    const err = step.validate?.();
    if (err) {
      setLocalErr(err);
      return;
    }
    if (last) onSubmit();
    else go(i + 1);
  };

  return (
    <div ref={topRef} className="card-warm p-6 sm:p-10 scroll-mt-28" data-testid={`${testPrefix}-wizard`}>
      <Steps steps={steps} current={i} />
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#B25B41]" data-testid={`${testPrefix}-step-counter`}>Step {i + 1} of {steps.length}</p>
      <div key={i} className="animate-step mt-2" data-testid={`${testPrefix}-step-${i + 1}`}>
        <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#1C2421]">{step.title}</h3>
        {step.description && <p className="text-sm text-[#5A6561] mt-2 max-w-2xl">{step.description}</p>}
        <div className="mt-8 space-y-8">{step.content}</div>
      </div>
      <div className="mt-8"><FormError message={localErr || error} testId={`${testPrefix}-error`} /></div>
      <div className="mt-8 flex items-center justify-between gap-3 border-t border-[#E5E0D8] pt-6">
        {i > 0 ? (
          <button type="button" onClick={() => go(i - 1)} className="btn-pill btn-outline" data-testid={`${testPrefix}-btn-prev`}>
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        ) : <span />}
        <button type="button" onClick={advance} disabled={submitting} className="btn-pill btn-primary disabled:opacity-60"
          data-testid={last ? `${testPrefix}-btn-submit` : `${testPrefix}-btn-next`}>
          {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : last ? submitLabel : <>Continue <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    </div>
  );
};
